import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { AnimatePresence, animate, motion, useDragControls, useMotionValue } from 'framer-motion'
import FloatingPanel from '@/components/ui/FloatingPanel'
import ButtonIcon from '@/components/ui/ButtonIcon'
import Modal from '@/layouts/modal/Modal'
import { DEFAULT_SHEET_TWEEN, SHEET_CLOSE_EASE } from '@/layouts/modal/sheetTiming'
import { CloseIcon } from '@/assets/icons'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import useBodyScrollLock from '@/util/scrollLock.js'
import { i18n } from '@/locale/i18n'

const SHEET_EXIT_TWEEN = { ...DEFAULT_SHEET_TWEEN, ease: SHEET_CLOSE_EASE }

/* Same thresholds as the app's `react-modal-sheet` sheets: a fast flick down, or a drag past
   60% of the sheet's height, closes it; anything less springs it back. */
const DRAG_CLOSE_VELOCITY = 500
const DRAG_CLOSE_THRESHOLD = 0.6

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const getFocusable = container =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => el.getClientRects().length > 0)

const MobileCalendarSheet = ({ isOpen, onClose, title, children }) => {
  const panelRef = useRef(null)
  const contentRef = useRef(null)
  const returnFocusRef = useRef(null)
  const y = useMotionValue(0)
  const dragControls = useDragControls()
  const [contentScrolls, setContentScrolls] = useState(false)

  useBodyScrollLock(isOpen)

  /* On a viewport too short for the calendar the body scrolls instead of dragging, so the
     confirm button stays reachable. Re-measured whenever the sheet or the viewport resizes. */
  useEffect(() => {
    if (!isOpen) return undefined

    const measure = () => {
      const content = contentRef.current
      if (content) setContentScrolls(content.scrollHeight > content.clientHeight + 1)
    }
    const frame = requestAnimationFrame(measure)
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (observer && contentRef.current) observer.observe(contentRef.current)
    window.addEventListener('resize', measure)

    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isOpen])

  /* Focus moves into the sheet when it opens and back to whatever opened it when it closes,
     so keyboard and screen-reader users land on the calendar and are not left on the page
     underneath. */
  useEffect(() => {
    if (!isOpen) return undefined

    returnFocusRef.current = document.activeElement
    const frame = requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }))

    return () => {
      cancelAnimationFrame(frame)
      const opener = returnFocusRef.current
      if (opener?.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true })
    }
  }, [isOpen])

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !panelRef.current) return

    const focusable = getFocusable(panelRef.current)
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  /* The header always drags, except from its close button; the body only while it has
     nothing to scroll. A tap that does not move still lands as a click. */
  const startDrag = event => dragControls.start(event)
  const startHeaderDrag = event => {
    if (event.target.closest?.('button')) return
    startDrag(event)
  }

  const handleDragEnd = (_, { velocity }) => {
    const height = panelRef.current?.offsetHeight || 0
    if (velocity.y > DRAG_CLOSE_VELOCITY || (height && y.get() / height > DRAG_CLOSE_THRESHOLD)) {
      onClose()
      return
    }
    animate(y, 0, DEFAULT_SHEET_TWEEN)
  }

  const modalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null
  if (!modalRoot) return null

  return ReactDOM.createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="calendar-sheet-backdrop"
            /* Same scrim as the app's other sheets: the library's 20% black, which the dark
               theme raises to 62% through the shared `.react-modal-sheet-backdrop` rule. */
            className="react-modal-sheet-backdrop fixed inset-0 z-[10000] bg-black/20"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: DEFAULT_SHEET_TWEEN }}
            exit={{ opacity: 0, transition: SHEET_EXIT_TWEEN }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      <FloatingPanel
        ref={panelRef}
        isOpen={isOpen}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        style={{ y, transformOrigin: 'bottom' }}
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0 }}
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%', transition: SHEET_EXIT_TWEEN }}
        transition={DEFAULT_SHEET_TWEEN}
        className="!fixed inset-x-0 bottom-0 !z-[10001] !w-full max-h-[90dvh] !rounded-t-[var(--radius-md)] !rounded-b-none !border-b-0 !overflow-hidden flex flex-col outline-none"
      >
        <header
          className="flex shrink-0 touch-none items-center justify-between gap-4 px-4 pt-4 pb-4"
          onPointerDown={startHeaderDrag}
        >
          <span className="text-base font-bold text-Neutral-6">{title}</span>
          <ButtonIcon
            variant="normal"
            size="medium"
            iconSize={20}
            cursorPointer
            highlightOnHover
            handleOnClick={onClose}
            ariaLabel={i18n('common.close')}
          >
            <CloseIcon />
          </ButtonIcon>
        </header>
        <div
          ref={contentRef}
          className={`min-h-0 flex-1 overscroll-contain px-4 pb-[calc(16px+env(safe-area-inset-bottom))] ${
            contentScrolls ? 'touch-pan-y overflow-y-auto' : 'touch-none overflow-hidden'
          }`}
          onPointerDown={contentScrolls ? undefined : startDrag}
        >
          {children}
        </div>
      </FloatingPanel>
    </>,
    modalRoot
  )
}

/**
 * Holds a calendar and its confirm action. Mobile gets a full-width bottom sheet on the
 * `FloatingPanel` surface, with the app's sheet motion and drag-down to close; desktop keeps
 * the centred `Modal`, configured through `modalProps`.
 */
const CalendarSheet = ({ isOpen, onClose, title, children, modalProps = {} }) => {
  const isMobileSize = useIsMobileSize()

  if (isMobileSize) {
    return (
      <MobileCalendarSheet isOpen={isOpen} onClose={onClose} title={title}>
        {children}
      </MobileCalendarSheet>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onCloseRequested={onClose}
      onClose={onClose}
      title={title}
      showCloseButton
      content={children}
      style={{ width: 'unset' }}
      headerClassList="pt-6 pl-6 pr-6 ml-0 mr-0"
      containerClass="pl-6 pr-6 ml-0 mr-0 max-h-[80vh] overflow-auto"
      {...modalProps}
    />
  )
}

export default CalendarSheet
